import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, type Card as ApiCard, type MovementsView, type Money, type Transaction, type TxPage } from "../api";
import { CardBrand } from "../components/CardBrand";
import { PageHead, Notice } from "../components/ui";
import { LateDays, SeriesMark, SituationMark, type Sit } from "../components/Situation";
import { fmtDay, formatMoney, todayISO } from "../lib/money";
import { useEntities } from "../entity";
import { usePeriod } from "../period";
import { useMe } from "../session";
import { useTxFlow } from "../txflow";

interface Totals { currency: "BRL" | "USD"; cashIn: Money; cashOut: Money; cashNet: Money; result: { income: Money; expenses: Money; net: Money } }

const MONTHS = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
/** Centavos inteiros -> texto decimal, apenas para exibir somas de visualização (gráficos). Os totais oficiais vêm do servidor. */
const dec = (cents: number) => { const neg = cents < 0; const a = Math.abs(Math.round(cents)); return `${neg ? "-" : ""}${Math.floor(a / 100)}.${String(a % 100).padStart(2, "0")}`; };
const brl = (cents: number) => formatMoney(dec(cents), "BRL");

interface Month { label: string; from: string; to: string; income: number; expenses: number }

function Card({ title, right, children, className }: { title: string; right?: React.ReactNode; children: React.ReactNode; className?: string }) {
  return (
    <section className={`card dash-card ${className ?? ""}`}>
      <header><h3>{title}</h3>{right}</header>
      {children}
    </section>
  );
}

/** Ordem padrão dos cards do painel (o usuário pode reorganizar; a ordem fica salva no navegador). */
const DEFAULT_ORDER = ["saude", "reservapf", "empresa", "empresacaixa", "resultado", "caixa", "receitasxdespesas", "saldoconta", "cartoes", "receitascat", "despesascat", "orcado", "proximos", "ultimos", "fluxo"];

interface BudgetView { totals: Record<"despesa" | "receita", { budget: Money; spent: Money; planned: Money }>; rows: { categoryId: string; groupName: string; name: string; nature: "receita" | "despesa"; budget: Money; spent: Money; pct: number | null; overPlanned: boolean }[];
  groups: { nature: "receita" | "despesa"; groupName: string; budget: Money; spent: Money; pct: number | null }[] }
/** Orçado × realizado do mês, com abertura por grupo e por categoria. Os valores e percentuais vêm do servidor (Planejamento → Orçamento). */
function BudgetCard({ realm, entityId, month, version }: { realm: string; entityId: string; month: string; version: number }) {
  const [b, setB] = useState<BudgetView | null>(null);
  const [open, setOpen] = useState(false);
  const [nat, setNat] = useState<"despesa" | "receita">("despesa");
  const [openGroups, setOpenGroups] = useState<string[]>([]);
  useEffect(() => { void api<BudgetView>("GET", `/api/${realm}/plan/budget?entityId=${entityId}&month=${month}`).then(setB).catch(() => setB(null)); }, [realm, entityId, month, version]);
  const t = b?.totals[nat];
  const groups = (b?.groups ?? []).filter((g) => g.nature === nat && g.budget.cents > 0);
  const bar = (pct: number | null, over: boolean) => pct === null ? null : <div className="bud-bar"><i style={{ width: `${Math.min(100, pct)}%`, background: over ? "var(--accent-despesa)" : "var(--accent-receita)" }} /></div>;
  return (
    <Card title="Orçado × realizado" right={<span><button className={`link small${nat === "despesa" ? " on" : ""}`} onClick={() => setNat("despesa")}>Despesas</button> · <button className={`link small${nat === "receita" ? " on" : ""}`} onClick={() => setNat("receita")}>Receitas</button> · <Link to="planejamento/orcamento" className="small">Orçamento</Link></span>}>
      {!b || !t || t.budget.cents === 0 ? <p className="muted">{b ? "Nenhum orçamento definido para este mês. Defina em Planejamento → Orçamento." : "Carregando…"}</p> : (
        <div data-budget-card>
          <div className="bud-head"><span>Orçado <b>{formatMoney(t.budget.decimal, "BRL")}</b></span><span>{nat === "despesa" ? "Gasto" : "Recebido"} <b>{formatMoney(t.spent.decimal, "BRL")}</b></span><span>Planejado <b>{formatMoney(t.planned.decimal, "BRL")}</b></span></div>
          {bar(t.budget.cents ? (t.spent.cents * 100) / t.budget.cents : null, nat === "despesa" && t.spent.cents > t.budget.cents)}
          <button className="link small" style={{ margin: "10px 0 6px" }} onClick={() => setOpen((v) => !v)} data-budget-open>{open ? "Ocultar categorias" : "Abrir por categorias"}</button>
          {open && (
            <table className="acc-bal" aria-label="Orçado por categoria">
              <thead><tr><th>Categoria</th><th className="num">Orçado</th><th className="num">{nat === "despesa" ? "Gasto" : "Recebido"}</th><th style={{ width: 120 }}>Uso</th></tr></thead>
              <tbody>{groups.map((g) => {
                const rows = (b.rows).filter((r) => r.nature === nat && r.groupName === g.groupName && (r.budget.cents > 0 || r.spent.cents > 0));
                const isOpen = openGroups.includes(g.groupName);
                return [
                  <tr key={g.groupName} className="bud-group"><td><button className="link" onClick={() => setOpenGroups((o) => (isOpen ? o.filter((x) => x !== g.groupName) : [...o, g.groupName]))} aria-expanded={isOpen}>{isOpen ? "▾" : "▸"} <b>{g.groupName}</b></button></td>
                    <td className="num tabular">{formatMoney(g.budget.decimal, "BRL")}</td><td className="num tabular">{formatMoney(g.spent.decimal, "BRL")}</td><td>{bar(g.pct, nat === "despesa" && (g.pct ?? 0) > 100)}</td></tr>,
                  ...(isOpen ? rows.map((r) => <tr key={r.categoryId}><td style={{ paddingLeft: 26 }}>{r.name}</td><td className="num tabular">{r.budget.cents ? formatMoney(r.budget.decimal, "BRL") : "—"}</td><td className="num tabular">{formatMoney(r.spent.decimal, "BRL")}</td><td>{bar(r.pct, nat === "despesa" && (r.pct ?? 0) > 100)}</td></tr>) : []),
                ];
              })}</tbody>
            </table>
          )}
        </div>
      )}
    </Card>
  );
}

/** Indicadores da empresa (PJ) no painel inicial: margens, resultado, equilíbrio, caixa, fôlego e inadimplência. O detalhe fica em Gestão do Negócio. */
interface PjKpis {
  margins: { bruta: number | null; operacional: number | null; liquida: number | null };
  revenue: { net: Money; result: Money };
  breakEven: { safetyPct: number | null; contributionPct: number | null };
  terms: { receivableOpen: Money; payableOpen: Money };
  delinquency: { overdue: Money; pct: number | null };
  cash: { balance: Money; burnNet: Money; generatesCash: boolean; runwayMonths: number | null };
}
const pctS = (p: number | null | undefined) => (p === null || p === undefined ? "—" : `${p.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`);
function usePjKpis(realm: string, entityId: string, from: string, to: string, version: number) {
  const [k, setK] = useState<PjKpis | null>(null);
  useEffect(() => { if (realm !== "pj" || !entityId) return; let live = true; void api<PjKpis>("GET", `/api/${realm}/rep/kpis?entityId=${entityId}&from=${from}&to=${to}&regime=competencia`).then((x) => { if (live) setK(x); }).catch(() => { if (live) setK(null); }); return () => { live = false; }; }, [realm, entityId, from, to, version]);
  return k;
}
function CompanyResultCard({ k }: { k: PjKpis | null }) {
  return (
    <Card title="Resultado da empresa" right={<Link to="empresa/dre" className="small">Ver DRE</Link>} className="c-company">
      {!k ? <p className="muted">Carregando…</p> : (
        <div className="co-grid" data-co-result>
          <div><div className="muted small">Receita líquida</div><div className="kpi-v tabular">{formatMoney(k.revenue.net.decimal, "BRL")}</div></div>
          <div><div className="muted small">Resultado do período</div><div className={`kpi-v tabular ${k.revenue.result.cents < 0 ? "neg" : "pos"}`}>{formatMoney(k.revenue.result.decimal, "BRL")}</div></div>
          <div><div className="muted small">Margem bruta</div><div className="tabular">{pctS(k.margins.bruta)}</div></div>
          <div><div className="muted small">Margem líquida</div><div className="tabular">{pctS(k.margins.liquida)}</div></div>
          <div><div className="muted small">Margem de contribuição</div><div className="tabular">{pctS(k.breakEven.contributionPct)}</div></div>
          <div><div className="muted small">Margem de segurança</div><div className={`tabular ${k.breakEven.safetyPct !== null && k.breakEven.safetyPct < 0 ? "neg" : ""}`}>{pctS(k.breakEven.safetyPct)}</div></div>
        </div>)}
    </Card>);
}
function CompanyCashCard({ k }: { k: PjKpis | null }) {
  return (
    <Card title="Caixa e recebíveis" right={<Link to="empresa/indicadores" className="small">Ver indicadores</Link>} className="c-company">
      {!k ? <p className="muted">Carregando…</p> : (
        <div className="co-grid" data-co-cash>
          <div><div className="muted small">Fôlego de caixa</div><div className="kpi-v tabular">{k.cash.generatesCash ? "Gera caixa" : k.cash.runwayMonths === null ? "—" : `${k.cash.runwayMonths.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} meses`}</div></div>
          <div><div className="muted small">Burn rate líquido</div><div className={`tabular ${k.cash.generatesCash ? "pos" : "neg"}`}>{k.cash.generatesCash ? "Positivo" : `${formatMoney(k.cash.burnNet.decimal, "BRL")}/mês`}</div></div>
          <div><div className="muted small">A receber em aberto</div><div className="tabular">{formatMoney(k.terms.receivableOpen.decimal, "BRL")}</div></div>
          <div><div className="muted small">A pagar em aberto</div><div className="tabular">{formatMoney(k.terms.payableOpen.decimal, "BRL")}</div></div>
          <div><div className="muted small">Vencido e não recebido</div><div className={`tabular ${k.delinquency.overdue.cents ? "neg" : ""}`}>{formatMoney(k.delinquency.overdue.decimal, "BRL")}</div></div>
          <div><div className="muted small">Inadimplência (12 meses)</div><div className="tabular">{pctS(k.delinquency.pct)}</div></div>
        </div>)}
    </Card>);
}

/** Indicadores pessoais (PF) no painel inicial: poupança, fixas × variáveis, comprometimento da renda, dívidas, gasto vs média e fôlego da reserva. */
interface PfKpis {
  income: Money; expenses: Money;
  saving: { amount: Money; rate: number | null };
  behavior: { fixed: Money; variable: Money; fixedPct: number | null };
  commitment: { fixedPct: number | null; debtPct: number | null; debt: Money };
  spending: { average3m: Money; vsAveragePct: number | null };
  reserve: { cash: Money; monthlyExpense: Money; months: number | null; window: { from: string; to: string } };
}
function usePfKpis(realm: string, entityId: string, from: string, to: string, version: number) {
  const [k, setK] = useState<PfKpis | null>(null);
  useEffect(() => { if (realm !== "pf" || !entityId) return; let live = true; void api<PfKpis>("GET", `/api/${realm}/rep/pf-kpis?entityId=${entityId}&from=${from}&to=${to}`).then((x) => { if (live) setK(x); }).catch(() => { if (live) setK(null); }); return () => { live = false; }; }, [realm, entityId, from, to, version]);
  return k;
}
function PfHealthCard({ k }: { k: PfKpis | null }) {
  return (
    <Card title="Saúde financeira do mês" className="c-company">
      {!k ? <p className="muted">Carregando…</p> : (
        <div className="co-grid" data-pf-health>
          <div><div className="muted small">Taxa de poupança</div><div className={`kpi-v tabular ${k.saving.rate !== null && k.saving.rate < 0 ? "neg" : "pos"}`}>{pctS(k.saving.rate)}</div></div>
          <div><div className="muted small">Sobra do período</div><div className={`tabular ${k.saving.amount.cents < 0 ? "neg" : ""}`}>{formatMoney(k.saving.amount.decimal, "BRL")}</div></div>
          <div><div className="muted small">Despesas fixas</div><div className="tabular">{formatMoney(k.behavior.fixed.decimal, "BRL")} <span className="muted small">({pctS(k.behavior.fixedPct)})</span></div></div>
          <div><div className="muted small">Despesas variáveis</div><div className="tabular">{formatMoney(k.behavior.variable.decimal, "BRL")}</div></div>
          <div><div className="muted small">Gasto vs. média de 3 meses</div><div className={`tabular ${k.spending.vsAveragePct !== null && k.spending.vsAveragePct > 0 ? "neg" : "pos"}`}>{k.spending.vsAveragePct === null ? "—" : `${k.spending.vsAveragePct > 0 ? "+" : ""}${pctS(k.spending.vsAveragePct)}`}</div></div>
          <div><div className="muted small">Média mensal (3 meses)</div><div className="tabular">{formatMoney(k.spending.average3m.decimal, "BRL")}</div></div>
        </div>)}
    </Card>);
}
function PfReserveCard({ k }: { k: PfKpis | null }) {
  return (
    <Card title="Reserva e compromissos" className="c-company">
      {!k ? <p className="muted">Carregando…</p> : (
        <div className="co-grid" data-pf-reserve>
          <div><div className="muted small">Fôlego da reserva</div><div className="kpi-v tabular">{k.reserve.months === null ? "—" : `${k.reserve.months.toLocaleString("pt-BR", { maximumFractionDigits: 1 })} meses`}</div></div>
          <div><div className="muted small">Saldo em contas</div><div className="tabular">{formatMoney(k.reserve.cash.decimal, "BRL")}</div></div>
          <div><div className="muted small">Fixas / renda</div><div className="tabular">{pctS(k.commitment.fixedPct)}</div></div>
          <div><div className="muted small">Dívidas / renda</div><div className={`tabular ${k.commitment.debtPct !== null && k.commitment.debtPct > 30 ? "neg" : ""}`}>{pctS(k.commitment.debtPct)}</div></div>
          <div><div className="muted small">Parcelas do período</div><div className="tabular">{formatMoney(k.commitment.debt.decimal, "BRL")}</div></div>
          <div><div className="muted small">Gasto mensal médio</div><div className="tabular">{formatMoney(k.reserve.monthlyExpense.decimal, "BRL")}</div></div>
        </div>)}
    </Card>);
}

function Kpi({ label, value, tone, sub }: { label: string; value: string; tone?: "pos" | "neg"; sub?: string }) {
  return (
    <div className="card kpi">
      <div className="muted small">{label}</div>
      <div className={`kpi-v tabular${tone ? ` ${tone}` : ""}`}>{value}</div>
      {sub && <div className="muted small">{sub}</div>}
    </div>
  );
}

function MonthBars({ months }: { months: Month[] }) {
  const max = Math.max(1, ...months.flatMap((m) => [m.income, m.expenses]));
  const W = 560, H = 190, base = 160, bw = 18, gap = W / months.length;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Receitas e despesas dos últimos meses" className="dash-svg">
      <line x1="0" x2={W} y1={base} y2={base} stroke="var(--border)" />
      {months.map((m, i) => {
        const cx = gap * i + gap / 2;
        const hi = (m.income / max) * 130, he = (m.expenses / max) * 130;
        return (
          <g key={m.from}>
            <rect x={cx - bw - 2} y={base - hi} width={bw} height={hi} rx="3" fill="var(--accent-receita)"><title>{`Receitas ${m.label}: ${brl(m.income)}`}</title></rect>
            <rect x={cx + 2} y={base - he} width={bw} height={he} rx="3" fill="var(--accent-despesa)"><title>{`Despesas ${m.label}: ${brl(m.expenses)}`}</title></rect>
            <text x={cx} y={base + 18} textAnchor="middle" fontSize="12" fill="var(--text-muted)">{m.label}</text>
          </g>
        );
      })}
    </svg>
  );
}

function Legend() {
  return <div className="dash-legend"><span><i style={{ background: "var(--accent-receita)" }} />Receitas</span><span><i style={{ background: "var(--accent-despesa)" }} />Despesas</span></div>;
}

const sitOf = (t: Transaction): Sit => (t.status === "cancelado" ? "cancelado" : t.status === "planejado" ? (t.overdue ? "vencido" : "agendado") : "realizado");
const PALETTE = ["#2f80ed", "#27ae60", "#f2994a", "#9b51e0", "#eb5757", "#00a6a6", "#d4a017", "#6c7a89"];

interface Slice { name: string; cents: number }
/** Rosca + lista com percentual e valor + total (receitas e despesas por categoria). */
function DonutList({ rows, total, label, empty }: { rows: Slice[]; total: number; label: string; empty: string }) {
  if (rows.length === 0) return <p className="muted">{empty}</p>;
  const R = 70, C = 2 * Math.PI * R;
  let acc = 0;
  return (
    <div className="donut-wrap" data-donut={label}>
      <svg viewBox="0 0 200 200" role="img" aria-label={`${label} por categoria`} className="donut">
        <circle cx="100" cy="100" r={R} fill="none" stroke="var(--surface-2)" strokeWidth="26" />
        {rows.map((r, i) => {
          const len = (r.cents / total) * C;
          const el = <circle key={r.name} cx="100" cy="100" r={R} fill="none" stroke={PALETTE[i % PALETTE.length]} strokeWidth="26" strokeDasharray={`${len} ${C - len}`} strokeDashoffset={-acc} transform="rotate(-90 100 100)"><title>{`${r.name}: ${brl(r.cents)}`}</title></circle>;
          acc += len;
          return el;
        })}
      </svg>
      <ul className="donut-list">
        {rows.map((r, i) => (
          <li key={r.name}><i style={{ background: PALETTE[i % PALETTE.length] }} /><span className="nm">{r.name}</span><span className="tabular pct">{((r.cents / total) * 100).toFixed(1).replace(".", ",")}%</span><span className="tabular val">{brl(r.cents)}</span></li>
        ))}
        <li className="tot"><span className="nm">Total</span><span className="tabular val">{brl(total)}</span></li>
      </ul>
    </div>
  );
}

function groupByCategory(items: Transaction[]): { rows: Slice[]; total: number } {
  const m = new Map<string, number>();
  for (const t of items) {
    if (t.currency !== "BRL") continue;
    if (t.splits?.length) { for (const sp of t.splits) { const n = sp.categoryPath.split(" › ").pop() ?? sp.categoryPath; m.set(n, (m.get(n) ?? 0) + sp.cents); } }   // pagamento de fatura: rateio por categoria das compras
    else if (t.treatment === "resultado") m.set(t.categoryName, (m.get(t.categoryName) ?? 0) + t.amount.cents);
  }
  const all = [...m].map(([name, cents]) => ({ name, cents })).sort((a, b) => b.cents - a.cents);
  const total = all.reduce((a, r) => a + r.cents, 0);
  const head = all.slice(0, 7), rest = all.slice(7).reduce((a, r) => a + r.cents, 0);
  return { rows: rest > 0 ? [...head, { name: "Outras", cents: rest }] : head, total };
}

/** Duas barras (A × B) e o resultado embaixo — usado em "Resultado do mês" e "Resultados de caixa". */
function PairBars({ a, b, aLabel, bLabel, ariaLabel }: { a: number; b: number; aLabel: string; bLabel: string; ariaLabel: string }) {
  const max = Math.max(1, a, b);
  const res = a - b;
  return (
    <div data-pair={ariaLabel}>
      {[[aLabel, a, "var(--accent-receita)"], [bLabel, b, "var(--accent-despesa)"]].map(([l, v, c]) => (
        <div className="pair-row" key={l as string}>
          <div className="row"><span>{l as string}</span><strong className="tabular">{brl(v as number)}</strong></div>
          <div className="bar"><div style={{ width: `${((v as number) / max) * 100}%`, background: c as string }} /></div>
        </div>
      ))}
      <div className="row pair-res"><span>Resultado</span><strong className={`tabular ${res < 0 ? "neg" : "pos"}`}>{brl(res)}</strong></div>
    </div>
  );
}

interface CashAccount { accountId: string; name: string; currency: "BRL" | "USD"; points: { date: string; balance: Money | null }[]; closing: Money | null }
interface Cashflow { from: string; to: string; accounts: CashAccount[] }

const fmtLong = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString("pt-BR", { day: "numeric", month: "short", year: "numeric" }).replace(/\./g, "");
const short = (cents: number) => { const a = Math.abs(cents) / 100; const t = a >= 1_000_000 ? `${(a / 1_000_000).toFixed(1)} mi` : a >= 1000 ? `${(a / 1000).toFixed(a >= 10_000 ? 0 : 1)} mil` : String(Math.round(a)); return `${cents < 0 ? "-" : ""}${t.replace(".", ",")}`; };

/** Fluxo de caixa projetado: UMA linha = soma dos saldos das contas marcadas (uma só marcada = o saldo dela). Antes da abertura da conta não há saldo. */
function CashflowChart({ data, to }: { data: Cashflow; to: string }) {
  const accs = data.accounts.filter((a) => a.currency === "BRL");
  const [off, setOff] = useState<string[]>([]);
  const [hover, setHover] = useState<number | null>(null);
  const on = accs.filter((a) => !off.includes(a.accountId));
  const days = accs[0]?.points.map((p) => p.date) ?? [];
  // soma só das contas marcadas que já existem no dia; se nenhuma existe, o dia fica sem ponto
  const total: (number | null)[] = days.map((_, i) => { let s = 0, n = 0; for (const a of on) { const b = a.points[i]?.balance; if (b) { s += b.cents; n++; } } return n ? s : null; });
  const vals = total.filter((v): v is number => v !== null);
  const lo = Math.min(0, ...vals), hi = Math.max(0, ...vals);
  const W = 640, H = 230, L = 46, R = 10, T = 12, B = 26, n = Math.max(1, days.length - 1);
  const x = (i: number) => L + (i / n) * (W - L - R);
  const y = (v: number) => T + (1 - (v - lo) / (hi - lo || 1)) * (H - T - B);
  const segs: string[] = []; let cur: string[] = [];
  total.forEach((v, i) => { if (v === null) { if (cur.length) segs.push(cur.join(" ")); cur = []; } else cur.push(`${x(i)},${y(v)}`); });
  if (cur.length) segs.push(cur.join(" "));
  const zy = y(0);
  const label = on.length === 1 ? on[0]!.name : on.length === accs.length ? "Total" : `${on.length} contas`;
  const moveTo = (e: React.MouseEvent<SVGSVGElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * W;
    setHover(Math.max(0, Math.min(days.length - 1, Math.round(((px - L) / (W - L - R)) * n))));
  };
  const hv = hover !== null ? total[hover] ?? null : null;
  if (accs.length === 0) return <p className="muted">Sem contas em reais para projetar.</p>;
  return (
    <div className="cf-wrap" data-cashflow>
      <table className="cf-table" aria-label="Contas no fluxo de caixa">
        <thead><tr><th /><th>Conta</th><th className="num">Saldo em {fmtDay(to).slice(0, 5)}</th></tr></thead>
        <tbody>
          {accs.map((a) => (
            <tr key={a.accountId}>
              <td><input type="checkbox" checked={!off.includes(a.accountId)} onChange={() => setOff((o) => (o.includes(a.accountId) ? o.filter((z) => z !== a.accountId) : [...o, a.accountId]))} aria-label={`Incluir ${a.name}`} /></td>
              <td>{a.name}</td>
              <td className={`num tabular nowrap ${a.closing && a.closing.cents < 0 ? "neg" : ""}`}>{a.closing ? formatMoney(a.closing.decimal, "BRL") : "—"}</td>
            </tr>
          ))}
          <tr className="total"><td /><td>Total (marcadas)</td><td className="num tabular nowrap">{brl(on.reduce((s, a) => s + (a.closing?.cents ?? 0), 0))}</td></tr>
        </tbody>
      </table>
      <div className="cf-chart">
        <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Fluxo de caixa projetado do mês" className="dash-svg" onMouseMove={moveTo} onMouseLeave={() => setHover(null)}>
          <defs>
            <clipPath id="cf-pos"><rect x="0" y="0" width={W} height={Math.max(0, zy)} /></clipPath>
            <clipPath id="cf-neg"><rect x="0" y={zy} width={W} height={Math.max(0, H - zy)} /></clipPath>
          </defs>
          {[hi, lo].filter((v, i, arr) => v !== 0 && arr.indexOf(v) === i).map((v) => <g key={v}><line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke="var(--border)" strokeDasharray="2 4" /><text x={L - 6} y={y(v) + 4} fontSize="11" textAnchor="end" fill="var(--text-muted)">{short(v)}</text></g>)}
          <line x1={L} x2={W - R} y1={zy} y2={zy} stroke="var(--text-muted)" strokeWidth="1.2" data-zero-line />
          <text x={L - 6} y={zy + 4} fontSize="11" textAnchor="end" fill="var(--text-muted)">0</text>
          {segs.map((pts, k) => (
            <g key={k} fill="none" strokeWidth="2.2" strokeLinejoin="round">
              <polyline points={pts} stroke="var(--accent-primary)" clipPath="url(#cf-pos)" />
              <polyline points={pts} stroke="var(--accent-despesa)" clipPath="url(#cf-neg)" />
            </g>
          ))}
          {hover !== null && hv !== null && <g><line x1={x(hover)} x2={x(hover)} y1={T} y2={H - B} stroke="var(--text-muted)" strokeDasharray="3 3" /><circle cx={x(hover)} cy={y(hv)} r="4.5" fill={hv < 0 ? "var(--accent-despesa)" : "var(--accent-primary)"} stroke="var(--surface)" strokeWidth="2" /></g>}
          {days.length > 0 && [0, Math.floor(days.length / 2), days.length - 1].map((i) => <text key={i} x={x(i)} y={H - 6} fontSize="11" textAnchor={i === 0 ? "start" : i === days.length - 1 ? "end" : "middle"} fill="var(--text-muted)">{fmtDay(days[i]!).slice(0, 5)}</text>)}
        </svg>
        {hover !== null && (
          <div className="cf-tip" style={{ left: `${Math.max(14, Math.min(86, (x(hover) / W) * 100))}%` }} role="status" data-cf-tip>
            <strong>{fmtLong(days[hover]!)}</strong>
            <div>{hv === null ? "Sem saldo (conta ainda não aberta)" : <>{label}: <b className="tabular">{brl(hv)}</b></>}</div>
          </div>
        )}
        {on.length === 0 && <p className="muted small">Marque ao menos uma conta para ver a linha.</p>}
      </div>
    </div>
  );
}

/** Cartões de crédito do ambiente: fatura atual, limite disponível e atalho para a fatura. */
function CardsCard({ cards }: { cards: ApiCard[] }) {
  return (
    <Card title="Cartões de crédito" right={<Link to="cartoes" className="small">Ver cartões</Link>} className="cards-card">
      {cards.length === 0 ? (
        <div data-cards-empty><p className="muted" style={{ margin: "0 0 8px" }}>Nenhum cartão cadastrado.</p><Link to="contas-financeiras" className="small">Cadastrar em Contas Financeiras</Link></div>
      ) : (
        <ul className="dash-list">
          {cards.map((c) => (
            <li key={c.id} data-card={c.name}>
              <div className="row"><span className="name-with-brand"><CardBrand brand={c.brand} size={30} /><Link to={`cartoes?card=${c.id}`}><strong>{c.name}</strong></Link></span><span className="muted small">Vence {fmtDay(c.currentInvoice.dueDate)}</span></div>
              <div className="row"><span>Fatura {c.currentInvoice.closed ? "fechada" : "atual"}</span><span className="tabular nowrap">{formatMoney(c.currentInvoice.remaining.decimal, "BRL")}</span></div>
              <div className="row"><span>Limite disponível</span><span className="tabular nowrap pos">{formatMoney(c.available.decimal, "BRL")}</span></div>
              <div className="row muted small"><span>Limite total</span><span className="tabular nowrap">{formatMoney(c.creditLimit.decimal, "BRL")}</span></div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function TxRow({ t }: { t: Transaction }) {
  const out = t.kind === "despesa";
  return (
    <li className="tx-row">
      <SituationMark sit={sitOf(t)} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div>{t.description}<SeriesMark kind={t.seriesKind} no={t.installmentNo} total={t.installmentTotal} /></div>
        <div className="muted small">{t.categoryName} · {t.accountName} · {fmtDay(t.settledDate ?? t.plannedDate)}{t.overdue && <LateDays planned={t.plannedDate} today={todayISO()} />}</div>
      </div>
      <div className={`tabular ${out ? "neg" : "pos"}`}>{out ? "-" : "+"}{formatMoney(t.amount.decimal, t.currency)}</div>
    </li>
  );
}

/** Início: visão geral do período selecionado no cabeçalho. Só mostra o que já tem dados no MM Money. */
export function ClientHome() {
  const { me } = useMe();
  const { current, loading } = useEntities();
  const flow = useTxFlow();
  const per = usePeriod();
  const realm = me.user.realm;
  const [state, setState] = useState<"loading" | "ok" | "error">("loading");
  const [bal, setBal] = useState<MovementsView["balances"] | null>(null);
  const [tot, setTot] = useState<Totals | null>(null);
  const [months, setMonths] = useState<Month[]>([]);
  const [spent, setSpent] = useState<Transaction[]>([]);
  const [earned, setEarned] = useState<Transaction[]>([]);
  const [upcoming, setUpcoming] = useState<Transaction[]>([]);
  const [recent, setRecent] = useState<Transaction[]>([]);
  const [cash, setCash] = useState<Cashflow | null>(null);
  const [cards, setCards] = useState<ApiCard[]>([]);
  const [hasData, setHasData] = useState(false);
  const pjk = usePjKpis(realm, current?.id ?? "", per.from, per.to, flow.version);
  const pfk = usePfKpis(realm, current?.id ?? "", per.from, per.to, flow.version);
  const hidKey = `mm.homeHiddenAccounts.${current?.id ?? ""}`;
  const [hiddenAccs, setHiddenAccs] = useState<Set<string>>(new Set());
  const [pickAccs, setPickAccs] = useState(false);
  const layoutKey = `mm.homeLayout.${current?.id ?? ""}`;
  const [order, setOrder] = useState<string[]>(DEFAULT_ORDER);
  const [edit, setEdit] = useState(false);
  const [drag, setDrag] = useState<string | null>(null);
  useEffect(() => {
    try { const saved = JSON.parse(localStorage.getItem(layoutKey) ?? "[]") as string[]; setOrder([...saved.filter((x) => DEFAULT_ORDER.includes(x)), ...DEFAULT_ORDER.filter((x) => !saved.includes(x))]); } catch { setOrder(DEFAULT_ORDER); }
  }, [layoutKey]);
  const saveOrder = (o: string[]) => { setOrder(o); try { localStorage.setItem(layoutKey, JSON.stringify(o)); } catch { /* sem armazenamento: vale só nesta sessão */ } };
  const moveBefore = (id: string, target: string) => { if (id === target) return; const o = order.filter((x) => x !== id); o.splice(o.indexOf(target), 0, id); saveOrder(o); };
  const step = (id: string, d: -1 | 1) => { const o = [...order], i = o.indexOf(id), j = i + d; if (j < 0 || j >= o.length) return; [o[i], o[j]] = [o[j]!, o[i]!]; saveOrder(o); };
  const resetLayout = () => { saveOrder(DEFAULT_ORDER); };
  useEffect(() => { try { setHiddenAccs(new Set(JSON.parse(localStorage.getItem(hidKey) ?? "[]") as string[])); } catch { setHiddenAccs(new Set()); } }, [hidKey]);
  const toggleAcc = (id: string) => setHiddenAccs((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); try { localStorage.setItem(hidKey, JSON.stringify([...n])); } catch { /* sem armazenamento: vale só nesta sessão */ } return n; });

  useEffect(() => {
    if (!current) return;
    let alive = true;
    setState("loading");
    const e = `entityId=${current.id}`;
    const base = `/api/${realm}`;
    void api<ApiCard[]>("GET", `${base}/cards?${e}&status=ativa`).then((l) => { if (alive) setCards(l); }).catch(() => { if (alive) setCards([]); });
    const ms = Array.from({ length: 6 }, (_, i) => {
      const d = new Date(per.period.getFullYear(), per.period.getMonth() - 5 + i, 1);
      return { label: MONTHS[d.getMonth()]!, from: iso(d), to: iso(new Date(d.getFullYear(), d.getMonth() + 1, 0)) };
    });
    (async () => {
      try {
        const [b, t, ...mt] = await Promise.all([
          api<MovementsView>("GET", `${base}/movements?${e}&from=${per.from}&to=${per.to}&limit=1`),
          api<Totals[]>("GET", `${base}/totals?${e}&from=${per.from}&to=${per.to}`),
          ...ms.map((m) => api<Totals[]>("GET", `${base}/totals?${e}&from=${m.from}&to=${m.to}`)),
        ]);
        const done = "status=confirmado,conciliado";
        const [sp, ea, up, rc, cf] = await Promise.all([
          api<TxPage>("GET", `${base}/transactions?${e}&${done}&kind=despesa&from=${per.from}&to=${per.to}&limit=200`),
          api<TxPage>("GET", `${base}/transactions?${e}&${done}&kind=receita&from=${per.from}&to=${per.to}&limit=200`),
          api<TxPage>("GET", `${base}/transactions?${e}&status=planejado&order=due&from=${per.from}&to=${per.to}&limit=8`),
          api<TxPage>("GET", `${base}/transactions?${e}&${done}&order=recent&limit=6`),
          api<Cashflow>("GET", `${base}/cashflow?${e}&from=${per.from}&to=${per.to}`),
        ]);
        if (!alive) return;
        setBal(b.balances);
        setTot(t.find((x) => x.currency === "BRL") ?? null);
        setMonths(ms.map((m, i) => { const r = mt[i]!.find((x) => x.currency === "BRL"); return { ...m, income: r?.result.income.cents ?? 0, expenses: r?.result.expenses.cents ?? 0 }; }));
        setSpent(sp.items); setEarned(ea.items); setUpcoming(up.items); setRecent(rc.items); setCash(cf);
        setHasData(b.balances.accounts.length > 0);
        setState("ok");
      } catch { if (alive) setState("error"); }
    })();
    return () => { alive = false; };
  }, [current, realm, per.from, per.to, per.period, flow.version]);

  const desp = useMemo(() => groupByCategory(spent), [spent]);
  const rec = useMemo(() => groupByCategory(earned), [earned]);

  if (loading) return <div className="muted">Carregando…</div>;
  if (!current) return <Notice kind="error">Nenhuma entidade disponível para o seu usuário.</Notice>;

  const canWrite = current.permission === "total";
  const first = me.user.displayName.split(" ")[0];
  const monthName = per.period.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
  const allActive = bal?.accounts.filter((a) => a.status === "ativa") ?? [];
  const active = allActive.filter((a) => !hiddenAccs.has(a.accountId));


  const widgets: Record<string, { span: number; node: React.ReactNode }> = bal ? {
    ...(realm === "pf" ? { saude: { span: 3, node: <PfHealthCard k={pfk} /> }, reservapf: { span: 3, node: <PfReserveCard k={pfk} /> } } : {}),
    ...(realm === "pj" ? { empresa: { span: 3, node: <CompanyResultCard k={pjk} /> }, empresacaixa: { span: 3, node: <CompanyCashCard k={pjk} /> } } : {}),
    resultado: { span: 2, node: <Card title="Resultado do mês" className="c-result"><PairBars ariaLabel="resultado" a={tot?.result.income.cents ?? 0} b={tot?.result.expenses.cents ?? 0} aLabel="Receitas" bLabel="Despesas" /></Card> },
    caixa: { span: 2, node: <Card title="Resultados de caixa" className="c-cash"><PairBars ariaLabel="caixa" a={tot?.cashIn.cents ?? 0} b={tot?.cashOut.cents ?? 0} aLabel="Entradas" bLabel="Saídas" /></Card> },
    receitasxdespesas: { span: 2, node: <Card title="Receitas × despesas" right={<Legend />}>
              {months.every((m) => m.income === 0 && m.expenses === 0) ? <p className="muted">Sem movimentações realizadas nos últimos 6 meses.</p> : <MonthBars months={months} />}
            </Card> },
    saldoconta: { span: 3, node: <Card title="Saldo por conta" right={<span><button className="link small" onClick={() => setPickAccs((v) => !v)} data-pick-accounts>{pickAccs ? "Concluir" : "Escolher contas"}</button> · <Link to="contas-extratos" className="small">Ver extratos</Link></span>}>
              {pickAccs && (
                <div className="small" style={{ marginBottom: 8 }} data-account-picker>
                  <div className="muted">Marque as contas que aparecem aqui (preferência deste navegador).</div>
                  {allActive.map((a) => <label key={a.accountId} style={{ display: "block" }}><input type="checkbox" checked={!hiddenAccs.has(a.accountId)} onChange={() => toggleAcc(a.accountId)} /> {a.name}</label>)}
                </div>
              )}
              <table className="acc-bal" aria-label="Saldo por conta" data-account-balances>
                <thead><tr><th>Conta</th><th className="num">Saldo atual</th><th className="num">Projetado</th></tr></thead>
                <tbody>
                  {active.map((a) => (
                    <tr key={a.accountId}><td>{a.name}</td><td className="num tabular nowrap">{formatMoney(a.current.decimal, a.currency)}</td><td className="num tabular nowrap">{formatMoney(a.projected.decimal, a.currency)}</td></tr>
                  ))}
                  {bal.totals.map((t) => (
                    <tr key={t.currency} className="total" data-account-total={t.currency}><td>Total{bal.totals.length > 1 ? ` (${t.currency})` : ""}{hiddenAccs.size > 0 ? " · todas as contas" : ""}</td><td className="num tabular nowrap">{formatMoney(t.current.decimal, t.currency)}</td><td className="num tabular nowrap">{formatMoney(t.projected.decimal, t.currency)}</td></tr>
                  ))}
                </tbody>
              </table>
            </Card> },
    cartoes: { span: 3, node: <CardsCard cards={cards} /> },
    receitascat: { span: 3, node: <Card title="Receitas por categoria"><DonutList rows={rec.rows} total={rec.total} label="Receitas" empty="Nenhuma receita realizada neste mês." /></Card> },
    despesascat: { span: 3, node: <Card title="Despesas por categoria"><DonutList rows={desp.rows} total={desp.total} label="Despesas" empty="Nenhuma despesa realizada neste mês." /></Card> },
    orcado: { span: 6, node: <BudgetCard realm={realm} entityId={current.id} month={per.from.slice(0, 7)} version={flow.version} /> },
    proximos: { span: 3, node: <Card title="Próximos vencimentos" right={<Link to="contas-pagar-receber" className="small">Ver todos</Link>}>
              {upcoming.length === 0 ? <p className="muted">Nada planejado para este mês.</p> : <ul className="dash-list">{upcoming.map((t) => <TxRow key={t.id} t={t} />)}</ul>}
            </Card> },
    ultimos: { span: 3, node: <Card title="Últimos lançamentos" right={canWrite ? <button className="btn ghost small-btn" onClick={() => flow.openNew("despesa")}>+ Novo</button> : undefined}>
              {recent.length === 0 ? <p className="muted">Ainda não há lançamentos realizados.</p> : <ul className="dash-list">{recent.map((t) => <TxRow key={t.id} t={t} />)}</ul>}
            </Card> },
    fluxo: { span: 6, node: <Card title="Fluxo de caixa" right={<span className="muted small">saldo projetado dia a dia</span>}>
            {cash ? <CashflowChart data={cash} to={per.to} /> : <p className="muted">Carregando…</p>}
          </Card> },
  } : {};

  return (
    <>
      <PageHead title={`Olá, ${first}`} sub={`Visão geral de ${monthName} · ${current.name}`} />
      {state === "error" && <Notice kind="error">Não foi possível carregar o painel. Tente novamente.</Notice>}
      {state === "loading" && !bal && <div className="muted">Carregando painel…</div>}
      {bal && !hasData && (
        <div className="card stub"><h3>Comece cadastrando uma conta</h3>
          <p className="muted" style={{ margin: "0 0 12px" }}>Cadastre uma conta financeira e lance receitas e despesas para ver o seu painel.</p>
          <Link className="btn primary" to="contas-financeiras">Ir para Contas Financeiras</Link></div>
      )}
      {bal && hasData && (
        <div className="dash">
          <div className="kpis">
            {bal.totals.map((t) => <Kpi key={t.currency} label={`Saldo atual${bal.totals.length > 1 ? ` (${t.currency})` : ""}`} value={formatMoney(t.current.decimal, t.currency)} sub={`Previsto no fim do mês: ${formatMoney(t.projected.decimal, t.currency)}`} />)}
          </div>

          <div className="dash-toolbar">
            <button className={`btn ghost small-btn${edit ? " on" : ""}`} onClick={() => setEdit((v) => !v)} data-layout-toggle>{edit ? "Concluir organização" : "Organizar painel"}</button>
            {edit && <><span className="muted small">Arraste os cards pela alça ⠿ (ou use as setas) para a posição que preferir. A ordem fica salva neste navegador.</span> <button className="link small" onClick={resetLayout} data-layout-reset>Restaurar ordem padrão</button></>}
          </div>
          <div className="dash-board" data-dash-board>
            {order.map((id) => { const w = widgets[id]; if (!w) return null; return (
              <div key={id} className={`dash-widget span-${w.span}${edit ? " editing" : ""}${drag === id ? " dragging" : ""}`} data-widget={id}
                draggable={edit} onDragStart={(e) => { setDrag(id); e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", id); }}
                onDragOver={(e) => { if (edit && drag && drag !== id) e.preventDefault(); }}
                onDrop={(e) => { e.preventDefault(); if (drag) moveBefore(drag, id); setDrag(null); }} onDragEnd={() => setDrag(null)}>
                {edit && <div className="dash-handle" aria-label="Mover card"><span title="Arraste para mover">⠿</span><button className="link" onClick={() => step(id, -1)} aria-label="Mover para antes">◀</button><button className="link" onClick={() => step(id, 1)} aria-label="Mover para depois">▶</button></div>}
                {w.node}
              </div>); })}
          </div>
        </div>
      )}
    </>
  );
}
